import React, {
    ReactElement,
    ReactNode,
    createContext,
    useCallback,
    useMemo,
    useState,
} from "react"

import { LCTOnlyDatasets } from "../../../../../../constants/enums"
import { AppError } from "../../../../../../errors/errorProcessors"
import usePublishLCTDeviceAmendment from "../../../../../../services/middleware/hooks/devices/lct/usePublishLCTDeviceAmendment"
import useSaveLCTDeviceDraft from "../../../../../../services/middleware/hooks/devices/lct/useSaveLCTDeviceDraft"
import { checkContextErrors } from "../../../../../../utils/registerDevice/registerDeviceUtils"
import { IDeviceFormContextValue } from "../../../../types/providerTypes"
import { HpDeviceFormData } from "../../../types/providerTypes"

export const AmendHpDeviceContext = createContext(
    {} as IDeviceFormContextValue<HpDeviceFormData>
)

type AmendHpDeviceProviderProps = {
    formData: HpDeviceFormData
    setFormData: React.Dispatch<React.SetStateAction<HpDeviceFormData>>
    formDataIsLoading: boolean
    systemReference: string
    formDataError?: AppError
    attachmentUUID?: string
    dataset?: LCTOnlyDatasets
    deviceContext?: React.Context<IDeviceFormContextValue<HpDeviceFormData>>
    children: ReactNode
}

const AmendHpDeviceProvider = ({
    formData,
    setFormData,
    formDataIsLoading,
    formDataError,
    systemReference,
    attachmentUUID,
    dataset = LCTOnlyDatasets.HP,
    deviceContext = AmendHpDeviceContext,
    children,
}: AmendHpDeviceProviderProps): ReactElement => {
    const [newDeviceId, setNewDeviceId] = useState<string | undefined>()
    const [newDeviceRef, setNewDeviceRef] = useState<string | undefined>()
    const [currentIndex, setCurrentIndex] = useState<number>(0)
    const [errors, setErrors] = useState<AppError[]>([])

    const {
        publishLCTDeviceAmendment,
        isLoading: publishInProgress,
        isLoaded: publishSuccessful,
        error: publishError,
    } = usePublishLCTDeviceAmendment(
        formData,
        dataset,
        systemReference,
        attachmentUUID
    )

    const {
        saveLCTDeviceDraft,
        isLoading: saveDraftInProgress,
        isLoaded: saveDraftSuccessful,
    } = useSaveLCTDeviceDraft<HpDeviceFormData>(
        true,
        dataset,
        systemReference,
        attachmentUUID
    )

    const onSubmit = useCallback(async (): Promise<void> => {
        const res = await publishLCTDeviceAmendment()
        setNewDeviceId(res.published_device_id)
        setNewDeviceRef(res.amendment_ref)
    }, [publishLCTDeviceAmendment])

    checkContextErrors([formDataError, publishError], errors, (error) =>
        setErrors((prev) => [...prev, error])
    )
    const setIndex = (step: number): void => {
        setErrors([])
        setCurrentIndex(step)
    }

    const value: IDeviceFormContextValue<HpDeviceFormData> = useMemo(
        () => ({
            attachmentUUID,
            formData,
            setFormData,
            currentIndex,
            setCurrentIndex: setIndex,
            deviceId: newDeviceId,
            deviceRef: newDeviceRef,
            onSubmit,
            submitLoading: publishInProgress,
            submitSuccessful: publishSuccessful,
            canEditManufacturer: false,
            formIsLoading: formDataIsLoading,
            saveDraft: saveLCTDeviceDraft,
            saveDraftLoading: saveDraftInProgress,
            saveDraftSuccessful,
            errors,
        }),
        [
            attachmentUUID,
            formData,
            setFormData,
            currentIndex,
            newDeviceId,
            newDeviceRef,
            onSubmit,
            publishInProgress,
            publishSuccessful,
            formDataIsLoading,
            saveLCTDeviceDraft,
            saveDraftInProgress,
            saveDraftSuccessful,
            errors,
        ]
    )

    const ContextProvider = deviceContext.Provider

    return <ContextProvider value={value}>{children}</ContextProvider>
}

AmendHpDeviceProvider.defaultProps = {
    // todo
    formDataError: undefined,
    attachmentUUID: undefined,
}

export default AmendHpDeviceProvider
